import {
    Badge,
    Text,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';
import {PlatformPropertyCard} from '@flexinet/ui-components';

import {
    delegationState,
    DnsZone,
} from '../types';
import {providerLabel} from '../providerLabels';

// The delegation set the provider publishes for this zone. `nameservers` is
// `[String!]!` — non-null — so an empty list is a real answer meaning the
// provider published none, not a field that failed to load.
const NameServers = ({nameservers}: { nameservers?: string[] | null }) => {

    if (!nameservers || nameservers.length === 0) {
        return (
            <Text size={'sm'} c={'dimmed'} fs={'italic'}>
                {I18n.get('This provider published no nameservers for the zone')}
            </Text>
        );
    }

    return (
        <>
            {nameservers.map(ns => (
                <Text key={ns} size={'xs'} ff={'monospace'}>{ns}</Text>
            ))}
        </>
    );
};

// ⚠️ THE TRI-STATE, SPELLED OUT IN FULL ON THE DETAIL PAGE.
//
// The table gets a badge; here there is room to say what the state actually
// means, and the UNDETERMINED case is where that matters. "Appears" is
// load-bearing in the schema: this is a read of the provider's own published
// nameservers, not a live resolver query against the registrar, so it can be
// confidently wrong in the minutes after a delegation change.
const Delegation = ({zone}: { zone: DnsZone }) => {

    const state = delegationState(zone);

    if (state === 'DELEGATED') {
        return (
            <>
                <Badge variant={'light'} color={'green'}>{I18n.get('Appears delegated')}</Badge>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('The registrar nameservers appear to match what this provider publishes.')}
                </Text>
            </>
        );
    }

    if (state === 'NOT_DELEGATED') {
        return (
            <>
                <Badge variant={'light'} color={'red'}>{I18n.get('Does not appear delegated')}</Badge>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('The registrar nameservers do not appear to match what this provider publishes. If you changed the delegation recently, this may catch up shortly.')}
                </Text>
            </>
        );
    }

    // UNDETERMINED — null or absent. NOT rendered as "not delegated".
    return (
        <>
            <Badge variant={'outline'} color={'gray'}>{I18n.get('Not determined')}</Badge>
            <Text size={'xs'} c={'dimmed'} mt={4}>
                {I18n.get('Delegation was not determined for this zone. This is not the same as "not delegated" — no conclusion was reached either way.')}
            </Text>
        </>
    );
};

export const Profile = ({record}: { record: DnsZone }) => {

    const properties = [
        {
            label: 'Name',
            value: record.name,
            span: 12,
        },
        {
            label: 'Provider',
            value: (
                <Badge variant={'light'}>
                    {providerLabel(record.provider)}
                </Badge>
            ),
            span: 12,
        },
        {
            label: 'Visibility',
            value: (
                <Badge variant={'light'} color={record.isPrivate ? 'orange' : 'blue'}>
                    {record.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
            span: 12,
        },
        {
            label: 'Delegation',
            value: <Delegation zone={record}/>,
            span: 12,
        },
        {
            label: 'Name Servers',
            value: <NameServers nameservers={record.nameservers}/>,
            span: 12,
        },
        {
            label: 'Connection',
            // The connection is shown because it is what makes the zone
            // ADDRESSABLE — the same zone name under two Route 53 connections
            // is two different zones.
            value: <Text size={'xs'} ff={'monospace'}>{record.connectionId}</Text>,
            span: 12,
        },
        // ⚠️ THE ZONE ID IS DELIBERATELY NOT SHOWN.
        //
        // It is OPAQUE — Flexi's own identifier, minted by the adapter, never
        // the provider's. Displaying it invites an operator to paste it into
        // the AWS or Cloudflare console, where it means nothing.
    ];

    return (
        <PlatformPropertyCard properties={properties}/>
    );
};
